import type { ReactEventHandler } from 'react';
import React, { useEffect, useRef } from 'react';
import { PAYMENT_REDIRECT_IFRAME_3DS_DATA } from 'src/constants/payment';
import { ERROR_TYPE } from 'src/store/error/constants';
import { PAY_ORDER, PAYMENT_ERRORS } from 'src/store/payment/constants';
import type {
  CardUniqueInfo,
  PaymentFailPayload,
} from 'src/store/payment/types';
import {
  popupClassNames,
  popupClassNamesMobile,
} from '../Payment3dsPopup/Payment3dsPopup';
import Popup from '../Popup';
import styles from './paymentHidden3dsPopup.module.css';

const iframeName = 'paymentIframe3dsMethod';

type Props = {
  isOpened: boolean;
  isMobile: boolean;
  data?: CardUniqueInfo['data3ds'];
  onFail: (error: PaymentFailPayload) => void;
  submitCres: (cres: string) => void;
  submitPares: (pares: string) => void;
  confirmPayment: () => void;
  closePopup: () => void;
};

export const PaymentHidden3dsPopup = (props: Props) => {
  const {
    data,
    submitCres,
    submitPares,
    confirmPayment,
    closePopup,
    onFail,
    isOpened,
    isMobile,
  } = props;

  const formRef = useRef<HTMLFormElement | null>(null);
  const firstTimeRef = useRef(false);
  const timeout = useRef<number | null>(null);

  useEffect(() => {
    if (!isOpened) return () => undefined;
    formRef.current?.submit();
    timeout.current = window.setTimeout(
      () =>
        onFail({
          type: ERROR_TYPE.ORDER_3DS,
          action: PAY_ORDER,
          message: PAYMENT_ERRORS.THREE_DS,
        }),
      10000,
    );
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, [onFail, isOpened]);

  if (
    !data ||
    !('processorIdentifier' in data) ||
    (data.processorIdentifier !== 'uniteller_v2' &&
      data.processorIdentifier !== 't_bank_v1' &&
      data.processorIdentifier !== 't_bank_v2')
  )
    return null;

  const onLoad: ReactEventHandler<HTMLIFrameElement> = (event) => {
    try {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }

      /**
       * Если мы открываем айфрейм с помощью урла и сами отправляем данные,
       * то нужно после этого вызвать запрос подтверждения платежа у банка
       */
      if ('data' in data && !firstTimeRef.current) {
        confirmPayment();
        // Не позволяем вызывать экшн confirmPayment более одного раза
        firstTimeRef.current = true;
        return;
      }

      /**
       *  Если пришел HTML ответ, то после загрузки айфрейма
       *  нужно вызвать ручку pay3ds и передать полученные данные
       */
      const { PaRes, cres } =
        // @ts-ignore
        event.target.contentWindow[PAYMENT_REDIRECT_IFRAME_3DS_DATA];
      if (PaRes) submitPares(PaRes);
      if (cres) submitCres(cres);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  return 'iframeInlineHTML' in data ? (
    <Popup
      classNames={isMobile ? popupClassNamesMobile : popupClassNames}
      open={isOpened}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      showCloseIcon={false}
      onClose={closePopup}
    >
      <iframe
        name={iframeName}
        id={iframeName}
        title={iframeName}
        width={500}
        height={550}
        srcDoc={data.iframeInlineHTML}
        onLoad={onLoad}
      />
    </Popup>
  ) : (
    <>
      <form
        target={iframeName}
        ref={formRef}
        method="post"
        action={data.url}
        className={styles.form}
      >
        <input type="hidden" name="threeDSMethodData" value={data.data} />
      </form>
      <iframe
        name={iframeName}
        id={iframeName}
        className={styles.form}
        title={iframeName}
        width={500}
        height={550}
        onLoad={onLoad}
      />
    </>
  );
};
